import { fg } from '@atlaskit/platform-feature-flags';

import type { StorageControlsPatternFormat } from '../../../types';
import { __internalGetPatternFormat, __internalSetPatternFormat } from '../bsc-global-state/core';
import { isSSR } from '../is-ssr';

/**
 * SSR-aware evaluation of the BSC flat pattern file format rollout.
 *
 * The frontend and SSR paths roll out on independent schedules (COOK-577): reading one shared
 * flag from both caused Confluence SSR/hydration mismatches. In an SSR context we read the
 * SSR-specific gate; otherwise we read the original frontend gate.
 *
 * Prefer {@link getFetchPatternFormat} over calling this directly — it evaluates this once per
 * initialization. `fg` is not stable within a process (an unresolved Statsig client returns `false`,
 * then `true`), so evaluating it separately at each site that needs the answer can produce
 * disagreeing results. Never call it to decide how to *read* a payload: read `patternFormat` off the
 * payload instead (COOK-632).
 */
export const isFlatPatternFileFormatEnabled = (): boolean =>
	isSSR()
		? fg('platform_bsc_ssr_flat_pattern_file_format')
		: fg('platform_bsc_flat_pattern_file_structure');

/**
 * Which category-file layout this process fetches.
 *
 * This is the only place the rollout gate is consulted for that purpose. The first caller to ask
 * decides, the answer is held in bsc-global-state, and every later caller reuses it — so two sites
 * cannot end up fetching different layouts because `fg` resolved differently between them. The first
 * caller is usually a `CacheLoader` built by `initializeControls`, but in Confluence's Bifrost worker
 * it is `getCategoryCacheUrlPerimeterSSR`, which runs while handling a request, long before
 * `initializeControls`.
 *
 * Nothing revises the decision, `initializeControls` included. If the first caller asks before the
 * flag client resolves, this process stays on that answer for its lifetime: consistently reading
 * payloads in the layout they were fetched in matters more than tracking the rollout promptly, because
 * a revised answer would leave already-fetched payloads being read as the other layout.
 */
export const getFetchPatternFormat = (): StorageControlsPatternFormat => {
	const decided = __internalGetPatternFormat();
	if (decided) {
		return decided;
	}

	const patternFormat = isFlatPatternFileFormatEnabled() ? 'flat' : 'trie';
	__internalSetPatternFormat(patternFormat);
	return patternFormat;
};
